import * as Sentry from '@sentry/angular';
import { init } from '@sentry/angular';

const errorCache = new Map();

const config = {
  errorCacheTTL: 60 * 60 * 1000, // 1 hour

  samplingRates: {
    fatal: 1.0, // 100% of fatal errors
    error: 1.0, // 100% of errors
    warning: 0, // drop warnings
    info: 0, // drop info events
    debug: 0, // drop debug events
  },

  maxCacheSize: 1000,
};

const enhancedBeforeSend = (event, environment) => {
  if (!environment.production) {
    return null;
  }

  const level = event.level || 'error';

  const samplingRate = config.samplingRates[level] || config.samplingRates.error;
  if (Math.random() > samplingRate) {
    return null;
  }

  if (level === 'error' || level === 'fatal') {
    try {
      const exception = event.exception?.values?.[0];
      if (!exception) return event; // If no exception data, just send it

      const fingerprint = [
        exception.type || '',
        exception.value || '',
        exception.stacktrace?.frames?.[0]?.filename || '',
        exception.stacktrace?.frames?.[0]?.function || '',
      ].join(':');

      const now = Date.now();

      if (errorCache.size > 0 && Math.random() < 0.1) {
        for (const [key, timestamp] of errorCache.entries()) {
          if (now - timestamp > config.errorCacheTTL) {
            errorCache.delete(key);
          }
        }
      }

      if (errorCache.has(fingerprint)) {
        const lastSeen = errorCache.get(fingerprint);
        if (now - lastSeen < config.errorCacheTTL) {
          return null; // Skip if we've seen this error recently
        }
      }

      errorCache.set(fingerprint, now);

      if (errorCache.size > config.maxCacheSize) {
        const oldestKey = Array.from(errorCache.keys())[0];
        errorCache.delete(oldestKey);
      }
    } catch (e) {
      console.warn('Error in Sentry beforeSend', e);
    }
  }

  return event;
};

/** Staging carries `production: true` to mirror prod error handling, so it is excluded by name. */
export const isSentryEnabled = (environment: { production: boolean; name: string }): boolean =>
  environment.production && environment.name !== 'staging';

// Sentry configuration options: https://docs.sentry.io/platforms/javascript/configuration/options/
export const initSentry = environment => {
  // Events and Session Replay are only sent from genuine production deployments, never staging or preview.
  const sentryEnabled = isSentryEnabled(environment);

  init({
    // Sends events only from production deployments; staging and preview stay silent
    enabled: sentryEnabled,
    // Associates events with the deployment environment (e.g. staging, production)
    environment: environment.name,
    // Tags events with the app version for tracking regressions across releases
    release: `isla-patient-submissions@${environment.version}`,
    // Data Source Name — the Sentry project endpoint to send events to
    dsn: environment.sentryDsn,
    // Enables capturing of custom metrics
    enableMetrics: true,
    // Enables structured logs to be sent to Sentry for searchable diagnostic context alongside errors and traces
    enableLogs: true,
    // Only sends error and fatal logs in production to minimise noise
    beforeSendLog: log => {
      if (environment.production && log.level !== 'error' && log.level !== 'fatal') {
        return null;
      }
      return log;
    },
    // Adds automatic performance instrumentation for page loads and navigations
    // Session Replay captures DOM snapshots to visually replay user sessions around errors
    integrations: [
      Sentry.browserTracingIntegration(),
      // Captures request/response bodies and headers for Firebase Storage requests in session replays to aid debugging upload failures
      // Only registered on production so staging sessions are never recorded.
      ...(sentryEnabled
        ? [
            Sentry.replayIntegration({
              // IMPORTANT: These three flags MUST remain true. They are the safe-by-default privacy baseline
              // that prevents patient PII (names, DOB, NHS numbers, form answers) and clinical media
              // (images, video, audio) from being captured in Session Replays.
              // Individual safe elements are selectively unmasked via data-sentry-unmask attributes in templates.
              // https://docs.sentry.io/platforms/javascript/session-replay/privacy/
              maskAllText: true,
              maskAllInputs: true,
              blockAllMedia: true,
              // Required in SDK v8+: unmask defaults to [] so data-sentry-unmask attributes are ignored unless explicitly configured
              // https://docs.sentry.io/platforms/javascript/session-replay/privacy/#unmask
              unmask: ['[data-sentry-unmask]'],
              networkDetailAllowUrls: [
                'https://firebasestorage.googleapis.com/v0/b/isla-portal-production.appspot.com/o',
                'https://firestore.googleapis.com/google.firestore.v1.Firestore/Listen/channel',
              ],
              networkRequestHeaders: ['X-Custom-Header'],
              networkResponseHeaders: ['X-Custom-Header'],
            }),
          ]
        : []),
    ],
    // Percentage of sessions recorded for general replay (0.0–1.0)
    replaysSessionSampleRate: 0,
    // Percentage of sessions with errors that are captured as replays (0.0–1.0); disabled off production
    replaysOnErrorSampleRate: sentryEnabled ? 1.0 : 0,
    // Limits which outgoing requests get Sentry trace headers attached
    tracePropagationTargets: ['localhost', environment.url],
    // Percentage of transactions captured for performance monitoring (0.0–1.0)
    tracesSampleRate: 0.2,
    // Drops error events whose message matches any of these patterns before sending
    ignoreErrors: [
      /Missing or insufficient permissions/i,
      /User not authenticated/i,
      /Login timeout/i,
      /Connection to Indexed Database server lost/i, // Expected on logout/tab close when IndexedDB connection is torn down mid-operation
      /deadline-exceeded/i, // Transient Firestore timeout (gRPC DEADLINE_EXCEEDED / HTTP 504). The SDK automatically reconnects listeners: https://firebase.google.com/docs/firestore/query-data/listen#handle_listen_errors
      /email address is already in use/i, // Expected when a user attempts to register with an email that already has an account (auth/email-already-in-use)
      /Unauthenticated access is not allowed/i, // Expected Firebase error when a user's session expires or they are logged out; the app redirects to login
      /auth\/invalid-credential/i, // Expected when a user enters incorrect login credentials or their session token has expired/been revoked
      /FirebaseError: internal/i, // Transient Firestore server error (gRPC INTERNAL). No app stack frames; the SDK reconnects automatically
      /User not signed in/i, // Expected when a user's session has expired or they are not authenticated; the app redirects to login
      /Importing a module script failed/i, // Browser module loading failure (e.g. network issues, cache staleness); no stacktrace, not actionable
      /auth\/expired-action-code/i, // Expected when a user clicks an expired email action link (e.g. password reset, email verification); the app shows an appropriate message
      /auth\/invalid-action-code/i, // Expected when a user clicks a malformed or already-used email action link (e.g. password reset, email verification); the app shows an appropriate message
      /^Load failed$/, // Unattributed WebKit network failure (no stack or caller), not patient-blocking; instrumented fetch failures report as "Load failed (<host>)" and are kept
      /^AbortError: Fetch is aborted$/, // Firestore WebChannel aborts on Safari ≤16, unhandled via Sentry Replay's response clone (WebKit tee bug); not patient-blocking
    ],
    // Custom pre-send hook for sampling, deduplication, and filtering
    beforeSend: event => enhancedBeforeSend(event, environment),
  });
};
